import type { ProjectRole } from '@overlayqa/shared';

/**
 * The Project Settings modal's tabs, and who may see each one.
 *
 * The gates are the boundaries the retired /projects/:id/settings page already
 * enforced, moved rather than redrawn:
 *   - Sharing is owner-only: ProjectSharingSection's mount-time GET
 *     /projects/:id/shares is owner-only and silently 403'd for everyone else
 *     (session 019ecc63, 2026-06-16).
 *   - Danger Zone is owner-only: Archive and Delete are refused by
 *     requireProjectOwner on the server.
 *   - A viewer gets nothing: PATCH /projects/:id refuses a viewer
 *     (requireProjectWrite), so both doors stay closed for one.
 * An unknown role is treated as not-owner, so no owner-only request fires on a
 * guess.
 */
export type SettingsTab = 'general' | 'sharing' | 'audits' | 'danger';

export const SETTINGS_TAB_LABELS: Record<SettingsTab, string> = {
  general: 'General',
  sharing: 'Sharing',
  audits: 'Audits',
  danger: 'Danger Zone',
};

type Role = ProjectRole | null | undefined;

export function canOpenProjectSettings(role: string | null | undefined): boolean {
  return role !== 'viewer';
}

export function visibleSettingsTabs(role: Role): SettingsTab[] {
  if (!canOpenProjectSettings(role)) return [];
  return role === 'owner' ? ['general', 'sharing', 'audits', 'danger'] : ['general', 'audits'];
}

/**
 * The tab a request opens. `accessibility` is the retired
 * /projects/:id/settings/accessibility deep link, whose content now lives on
 * Audits. Anything the caller cannot see, or does not exist, opens General.
 */
export function resolveSettingsTab(requested: string | null | undefined, role: Role): SettingsTab {
  const wanted = requested === 'accessibility' ? 'audits' : requested;
  const visible = visibleSettingsTabs(role);
  return visible.find((tab) => tab === wanted) ?? 'general';
}
